import { AuditRow, DaySummaryRow, TripDetailRow } from "../models/AuditRow";
import { AuditTrip, displayReason, RecordType } from "../models/AuditTrip";
import { dayKeyInZone, startOfDayInZone } from "./timeZone";
import { measuredConsumption } from "./tripConsumption";

export function dayRowId(deviceId: string, dateKey: string) {
  return `day:${deviceId}:${dateKey}`;
}

const RECORD_TYPE_ORDER: RecordType[] = ["digital", "analog"];

/**
 * Groups entries into one summary row per vehicle and local calendar day, each followed by its
 * entry rows (tree data paths). Rows come back in the table's default order: vehicles
 * alphabetical, each vehicle's days oldest first, the entries of a day chronological. "Load more"
 * takes the day rows in this order. Every figure of the day row is the sum of its listed entries;
 * Geotab's whole-day consumption is not mixed in.
 */
export function buildDaySummaryRows(
  trips: AuditTrip[],
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): AuditRow[] {
  const days = new Map<string, DaySummaryRow>();

  const sorted = [...trips].sort((a, b) => a.start.getTime() - b.start.getTime());
  for (const trip of sorted) {
    const dateKey = dayKeyInZone(trip.start, timeZone);
    const id = dayRowId(trip.device.id, dateKey);
    let day = days.get(id);
    if (!day) {
      day = {
        rowType: "day",
        id,
        path: [id],
        device: trip.device,
        dateKey,
        date: startOfDayInZone(trip.start, timeZone),
        distance: undefined,
        firstStart: trip.start,
        lastStop: undefined,
        firstTrip: trip,
        lastTrip: trip,
        consumption: undefined,
        reasons: [],
        hasMissingReason: false,
        dutyRosterNumbers: [],
        driverIds: [],
        durationMs: 0,
        refuelLiters: undefined,
        adBlueLiters: undefined,
        odometerStartKm: undefined,
        odometerEndKm: undefined,
        recordTypes: [],
        trips: [],
      };
      days.set(id, day);
    }
    day.trips.push(trip);
    day.lastTrip = trip;
    if (trip.distance != null && Number.isFinite(trip.distance)) day.distance = (day.distance ?? 0) + trip.distance;
    if (trip.stop && (!day.lastStop || trip.stop > day.lastStop)) day.lastStop = trip.stop;
    if (trip.durationMs != null) day.durationMs += trip.durationMs;
    if (trip.consumption != null) day.consumption = (day.consumption ?? 0) + trip.consumption;
    if (trip.refuelLiters != null) day.refuelLiters = (day.refuelLiters ?? 0) + trip.refuelLiters;
    if (trip.adBlueLiters != null) day.adBlueLiters = (day.adBlueLiters ?? 0) + trip.adBlueLiters;
    const shown = displayReason(trip);
    if (shown) {
      if (!day.reasons.some(r => r.label === shown)) day.reasons.push({ label: shown, taxClass: trip.taxClass });
    } else {
      day.hasMissingReason = true;
    }
    const roster = trip.dutyRoster?.dutyRosterNumber;
    if (roster && !day.dutyRosterNumbers.includes(roster)) day.dutyRosterNumbers.push(roster);
    if (trip.driverId && !day.driverIds.includes(trip.driverId)) day.driverIds.push(trip.driverId);
    if (!day.recordTypes.includes(trip.recordType)) day.recordTypes.push(trip.recordType);
  }

  const dayRows = [...days.values()].sort(
    (a, b) => a.device.name.localeCompare(b.device.name, "de") || a.dateKey.localeCompare(b.dateKey),
  );

  const rows: AuditRow[] = [];
  for (const day of dayRows) {
    day.recordTypes.sort((a, b) => RECORD_TYPE_ORDER.indexOf(a) - RECORD_TYPE_ORDER.indexOf(b));
    day.consumption = measuredConsumption(day.consumption, day.distance);
    day.odometerStartKm = day.firstTrip.odometerStartKm;
    day.odometerEndKm = day.lastTrip.odometerEndKm;
    rows.push(day);
    for (const trip of day.trips) {
      const detail: TripDetailRow = {
        rowType: "trip",
        id: trip.id,
        path: [day.id, trip.id],
        device: day.device,
        // The day, not the entry start: sorting by date keeps a day's entries together.
        date: day.date,
        trip,
      };
      rows.push(detail);
    }
  }
  return rows;
}
